import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Shell from "@/layout/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Network, Plus, Trash2, ShieldAlert, Layers } from "lucide-react";

const TYPES = [
  { v: "central", l: "Central IT Admin" },
  { v: "coe", l: "Controller of Examinations" },
  { v: "dean", l: "Dean (e.g. Student Welfare)" },
  { v: "hod", l: "Head of Department (HOD)" },
  { v: "custom", l: "Custom authority" },
];

export default function AuthoritiesPage() {
  const [institutes, setInstitutes] = useState([]);
  const [instId, setInstId] = useState("");
  const [auths, setAuths] = useState([]);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nf, setNf] = useState({ type: "coe", name: "", department: "", holder_id: "" });

  useEffect(() => {
    api.saOverview().then((r) => {
      const list = r.institutes || [];
      setInstitutes(list);
      if (list[0]) setInstId(String(list[0].id));
    }).catch(() => {});
  }, []);

  const load = async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [a, c, u] = await Promise.all([
        api.saAuthorities(id),
        api.categories({ institute_id: id, all: 1 }),
        api.users({ institute_id: id, role: "team", limit: 500 }),
      ]);
      setAuths(a.authorities || []);
      setCats(c.categories || []);
      setUsers(u.users || u || []);
    } catch (e) { toast.error(e.message || "Load failed"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (instId) load(instId); }, [instId]);

  const createAuth = async () => {
    if (!nf.name.trim()) return toast.error("Name is required");
    if (nf.type === "hod" && !nf.department.trim()) return toast.error("HOD needs a department");
    try {
      await api.saCreateAuthority({
        institute_id: instId, type: nf.type, name: nf.name.trim(),
        department: nf.type === "hod" ? nf.department.trim() : undefined,
        holder_id: nf.holder_id || undefined,
      });
      toast.success("Authority created");
      setNf({ type: "coe", name: "", department: "", holder_id: "" });
      load(instId);
    } catch (e) { toast.error(e.message || "Create failed"); }
  };

  const setHolder = async (id, holder_id) => {
    try { await api.saUpdateAuthority(id, { holder_id }); toast.success("Holder updated"); load(instId); }
    catch (e) { toast.error(e.message); }
  };
  const delAuth = async (id) => {
    if (!window.confirm("Delete this authority? Its categories will become unassigned.")) return;
    try { await api.saDeleteAuthority(id); toast.success("Deleted"); load(instId); }
    catch (e) { toast.error(e.message); }
  };

  const setCatOwner = async (catId, patch) => {
    try { await api.saUpdateCategoryOwnership(catId, patch); load(instId); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Shell role="superadmin" title="Authorities & Routing" subtitle="Define who owns which tickets, confidentiality, and escalation.">
      <div className="space-y-6">
        <div className="glass rounded-2xl p-4 flex items-center gap-3">
          <Network className="h-5 w-5 text-cobalt-500" />
          <div className="flex-1">
            <div className="overline mb-1">Institute</div>
            <Select value={instId} onValueChange={setInstId}>
              <SelectTrigger className="w-72 rounded-xl"><SelectValue placeholder="Pick institute" /></SelectTrigger>
              <SelectContent>
                {institutes.map((i) => <SelectItem key={i.id} value={String(i.id)}>{i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="overline mb-3">Add authority</div>
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <Label>Type</Label>
              <Select value={nf.type} onValueChange={(v) => setNf((s) => ({ ...s, type: v }))}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={nf.name} onChange={(e) => setNf((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Dean, Student Welfare" className="mt-1 rounded-xl" />
            </div>
            {nf.type === "hod" && (
              <div>
                <Label>Department</Label>
                <Input value={nf.department} onChange={(e) => setNf((s) => ({ ...s, department: e.target.value }))}
                  placeholder="CSE" className="mt-1 rounded-xl uppercase" />
              </div>
            )}
            <div>
              <Label>Holder (notified person)</Label>
              <Select value={nf.holder_id || "none"} onValueChange={(v) => setNf((s) => ({ ...s, holder_id: v === "none" ? "" : v }))}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue placeholder="Assign later" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Assign later</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.name} · {u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={createAuth} className="mt-3 rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5">
            <Plus className="h-4 w-4" /> Create authority
          </Button>
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="overline mb-3">Authorities · {auths.length}</div>
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
            <div className="space-y-2">
              {auths.length === 0 && <div className="text-sm text-muted-foreground">No authorities yet. Add one above.</div>}
              {auths.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-xl border p-3">
                  <div className="h-9 w-9 rounded-lg bg-cobalt-500/10 text-cobalt-500 grid place-items-center text-xs font-mono uppercase">
                    {a.type === "hod" ? (a.department || "HOD") : a.type.slice(0, 3)}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{a.name}{a.department ? ` · ${a.department}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{a.holder?.name ? `Holder: ${a.holder.name}` : "No holder assigned"}</div>
                  </div>
                  <Select value={a.holder_id ? String(a.holder_id) : "none"} onValueChange={(v) => setHolder(a.id, v === "none" ? null : v)}>
                    <SelectTrigger className="w-52 rounded-xl h-9"><SelectValue placeholder="Assign holder" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No holder —</SelectItem>
                      {users.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {a.type !== "central" && (
                    <Button variant="outline" size="sm" onClick={() => delAuth(a.id)}
                      className="rounded-full text-red-600 border-red-200 hover:bg-red-50 gap-1.5">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="h-4 w-4 text-cobalt-500" />
            <div className="overline">Category ownership</div>
          </div>
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.id} className="grid md:grid-cols-[1.4fr_1.2fr_auto_auto] gap-3 items-center rounded-xl border p-3">
                <div className="text-sm font-medium flex items-center gap-2">
                  {c.is_confidential && <ShieldAlert className="h-3.5 w-3.5 text-amber-500" />}
                  {c.name}
                </div>
                <Select value={c.authority_id ? String(c.authority_id) : "none"} onValueChange={(v) => setCatOwner(c.id, { authority_id: v === "none" ? null : Number(v) })}>
                  <SelectTrigger className="rounded-xl h-9"><SelectValue placeholder="Owner authority" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {auths.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.name}{a.department ? ` (${a.department})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!c.is_confidential} onChange={(e) => setCatOwner(c.id, { is_confidential: e.target.checked })} className="accent-amber-500" />
                  Confidential
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!c.allow_anonymous} onChange={(e) => setCatOwner(c.id, { allow_anonymous: e.target.checked })} className="accent-cobalt-500" />
                  Anonymous
                </label>
              </div>
            ))}
            {cats.length === 0 && <div className="text-sm text-muted-foreground">No categories in this institute yet.</div>}
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Confidential categories are visible only to their owning authority (and superadmin). Anonymous lets requesters file without identifying themselves.
          </p>
        </div>
      </div>
    </Shell>
  );
}
